import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { listSignups, getSignupsCount } from "../lib/api";
import { HTTP_UNAUTHORIZED } from "../constants/http";

// Encapsulates the data-loading lifecycle for the admin dashboard.
// Calls onUnauthorized() when the API rejects the key so the parent can
// trigger sign-out.
export function useSignupsData(adminKey, onUnauthorized) {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, displayed_total: 0 });
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!adminKey) return;
    setLoading(true);
    try {
      const [list, count] = await Promise.all([
        listSignups(adminKey, 500),
        getSignupsCount(),
      ]);
      setItems(Array.isArray(list?.items) ? list.items : []);
      setStats({
        total: count?.total ?? 0,
        displayed_total: count?.displayed_total ?? 0,
      });
    } catch (err) {
      const status = err?.response?.status;
      if (status === HTTP_UNAUTHORIZED) {
        toast.error("Session expired. Please sign in again.");
        if (typeof onUnauthorized === "function") {
          onUnauthorized();
        }
      } else {
        console.error("Failed to load signups:", err);
        toast.error("Could not load signups.");
      }
    } finally {
      setLoading(false);
    }
  }, [adminKey, onUnauthorized]);

  useEffect(() => {
    if (adminKey) {
      load();
    } else {
      // Clear data on sign-out.
      setItems([]);
      setStats({ total: 0, displayed_total: 0 });
    }
  }, [adminKey, load]);

  return { items, stats, loading, reload: load };
}
